import { isEqual } from "lodash";
import React, { useContext, useEffect, useState } from "react";

import { get } from "../../common/utils/api";
import {
  getItemFromStorage,
  removeItemFromStorage,
  setItemInStorage,
} from "../../common/utils/helpers";
import { db } from "../../data/utils/data";

export const USER_STORAGE_KEY = "user";
export const ORIGINAL_ADMIN_TOKEN_KEY = "original_admin_token";
export const IMPERSONATED_USER_KEY = "impersonated_user";

const UserContext = React.createContext({
  user: null,
  login: async () => {},
  refresh: async () => {},
  logout: () => {},
});

export const UserProvider = (props) => {
  const userCache = getItemFromStorage(USER_STORAGE_KEY) || null;
  const [user, setUser] = useState(userCache);

  const saveUser = (newUser) => {
    setUser((prevUser) => (isEqual(prevUser, newUser) ? prevUser : newUser));
    setItemInStorage(USER_STORAGE_KEY, newUser);
  };

  // Use token to get current user information and save it
  const login = async (token) => {
    return get("/users/current/", token, false).then((response) => {
      saveUser({ token, user: response.data });
    });
  };
  // Save refreshed user data or use cached token to get it if not supplied
  const refresh = async (newUser) => {
    if (newUser) {
      saveUser({ token: user.token, user: newUser });
    } else {
      await login(user.token);
    }
  };
  // Delete saved user state
  const logout = () => {
    setUser(null);
    removeItemFromStorage(USER_STORAGE_KEY);
  };

  // Periodically refresh the user information
  useEffect(() => {
    const update = () => {
      if (user && navigator.onLine) {
        refresh().catch(() => null);
      }
    };

    update();
    // Refresh every day - logout/in to force a refresh faster
    const interval = setInterval(update, 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <UserContext.Provider value={{ user, login, logout, refresh }} {...props} />
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const getToken = () => {
  return (getItemFromStorage(USER_STORAGE_KEY) || {}).token;
};

const clearUserCachedData = async () => {
  try {
    // Clear all user-specific tables but keep appInfo
    await Promise.all([db.data.clear(), db.images.clear()]);
    console.info("Cleared user cached data from IndexedDB");
  } catch (error) {
    console.error("Error clearing user cached data:", error);
  }
};

export const switchToUser = (userToken, userInfo) => {
  const currentToken = getToken();
  setItemInStorage(ORIGINAL_ADMIN_TOKEN_KEY, currentToken);
  if (userInfo) {
    setItemInStorage(IMPERSONATED_USER_KEY, {
      first_name: userInfo.first_name,
      last_name: userInfo.last_name,
      email: userInfo.email,
    });
  }

  const userData = getItemFromStorage(USER_STORAGE_KEY);
  if (userData) {
    userData.token = userToken;
    if (userInfo) {
      userData.user = userInfo;
    }
    setItemInStorage(USER_STORAGE_KEY, userData);
  }
  // Reload the page to fetch data with the new user's identity
  window.location.reload();
};

export const exitImpersonation = async () => {
  const originalToken = getItemFromStorage(ORIGINAL_ADMIN_TOKEN_KEY);

  if (originalToken) {
    // Clear all user-specific cached data first (IndexedDB)
    await clearUserCachedData();

    // Restore the original token
    const userData = getItemFromStorage(USER_STORAGE_KEY);
    if (userData) {
      userData.token = originalToken;
      setItemInStorage(USER_STORAGE_KEY, userData);
    }

    removeItemFromStorage(ORIGINAL_ADMIN_TOKEN_KEY);
    removeItemFromStorage(IMPERSONATED_USER_KEY);

    // Reload to fetch data with the admin's identity
    window.location.reload();
  }
};

export const isImpersonating = () => {
  return getItemFromStorage(ORIGINAL_ADMIN_TOKEN_KEY) !== null;
};

export const getImpersonatedUser = () => {
  return getItemFromStorage(IMPERSONATED_USER_KEY);
};
